import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { generateId } from '@/lib/format';
import { Account, Transaction } from '@/lib/types';
import { toast } from 'sonner';

const BASIQ_LAST_SYNC_KEY = 'aussibooks_basiq_last_sync';

interface BasiqConnection {
  id: string;
  status: string;
  institution: string;
  institutionLogo: string | null;
  lastUpdated: string;
}

interface BasiqAccount {
  basiqAccountId: string;
  name: string;
  type: string;
  balance: number;
  institution: string;
  accountNo: string;
}

interface SyncResult {
  imported: number;
  skipped: number;
  accountsLinked: number;
}

export function useBasiqSync() {
  const { data, updateData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState<BasiqConnection[]>([]);
  const [basiqAccounts, setBasiqAccounts] = useState<BasiqAccount[]>([]);
  const [basiqUserId, setBasiqUserId] = useState<string | null>(null);

  const lastSync = localStorage.getItem(BASIQ_LAST_SYNC_KEY);

  // Load basiqUserId from database on mount
  useEffect(() => {
    // Migrate from localStorage if present
    const legacyId = localStorage.getItem('aussibooks_basiq_user_id');
    if (legacyId) {
      setBasiqUserId(legacyId);
      // Persist to DB and clean up localStorage
      supabase.from('profiles' as any).update({ basiq_user_id: legacyId } as any)
        .eq('user_id', (supabase.auth as any).__currentUser?.id ?? '')
        .then(() => localStorage.removeItem('aussibooks_basiq_user_id'));
      return;
    }
    // Load from DB
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('profiles').select('basiq_user_id').eq('user_id', user.id).single()
        .then(({ data: profile }) => {
          if ((profile as any)?.basiq_user_id) {
            setBasiqUserId((profile as any).basiq_user_id);
          }
        });
    });
  }, []);

  const invoke = useCallback(async (body: Record<string, unknown>) => {
    const { data: result, error } = await supabase.functions.invoke('basiq-sync', { body });
    if (error) throw error;
    return result;
  }, []);

  // Fetch mobile from user_pii (secure PII table)
  const fetchMobile = useCallback(async (): Promise<string | undefined> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return undefined;
    const { data: pii } = await supabase
      .from('user_pii' as any)
      .select('mobile')
      .eq('user_id', user.id)
      .single();
    return (pii as any)?.mobile || undefined;
  }, []);

  // Step 1: Create Basiq user (one-time)
  const createUser = useCallback(async (mobile?: string) => {
    setLoading(true);
    try {
      const result = await invoke({ action: 'create-user', mobile });
      const newBasiqUserId = result.basiqUserId as string;
      setBasiqUserId(newBasiqUserId);
      toast.success('Bank sync user created');
      return newBasiqUserId;
    } catch (e: any) {
      toast.error('Failed to initialise bank sync');
      throw e;
    } finally {
      setLoading(false);
    }
  }, [invoke]);

  // Step 2: Get consent link to open in new tab
  const getConsentLink = useCallback(async (userId?: string) => {
    const uid = userId || basiqUserId;
    if (!uid) throw new Error('No Basiq user');
    setLoading(true);
    try {
      const result = await invoke({ action: 'consent-link', basiqUserId: uid });
      return result.consentUrl as string;
    } finally {
      setLoading(false);
    }
  }, [invoke, basiqUserId]);

  // Step 3: Check status of connections
  const checkStatus = useCallback(async () => {
    if (!basiqUserId) return;
    setLoading(true);
    try {
      const result = await invoke({ action: 'status', basiqUserId });
      setConnections(result.connections ?? []);
      setBasiqAccounts(result.accounts ?? []);
    } catch {
      toast.error('Failed to check bank status');
    } finally {
      setLoading(false);
    }
  }, [invoke, basiqUserId]);

  // Step 4: Sync transactions
  const syncTransactions = useCallback(async (): Promise<SyncResult | null> => {
    if (!basiqUserId) {
      toast.error('Connect a bank first');
      return null;
    }
    setLoading(true);
    try {
      const result = await invoke({
        action: 'sync',
        basiqUserId,
        sinceDate: lastSync ? lastSync.slice(0, 10) : undefined,
      });

      const basiqTxns: any[] = result.transactions ?? [];
      const basiqAccts: BasiqAccount[] = result.accounts ?? [];

      let accountsLinked = 0;
      const accountMap: Record<string, string> = {};

      await updateData(prev => {
        const newAccounts = [...prev.accounts];
        const newTransactions = [...prev.transactions];

        for (const ba of basiqAccts) {
          const existing = newAccounts.find(
            a => a.name === `${ba.institution} ${ba.name}` || a.name === ba.name
          );
          if (existing) {
            accountMap[ba.basiqAccountId] = existing.id;
          } else {
            const newId = generateId();
            newAccounts.push({
              id: newId,
              name: `${ba.institution} ${ba.name}`.trim(),
              type: ba.type as any,
              initialBalance: 0,
              createdAt: new Date().toISOString(),
            });
            accountMap[ba.basiqAccountId] = newId;
            accountsLinked++;
          }
        }

        const existingBasiqIds = new Set(
          newTransactions
            .filter(t => t.description.startsWith('[basiq:'))
            .map(t => {
              const match = t.description.match(/^\[basiq:([^\]]+)\]/);
              return match ? match[1] : null;
            })
            .filter(Boolean)
        );

        let imported = 0;
        let skipped = 0;

        for (const bt of basiqTxns) {
          if (existingBasiqIds.has(bt.basiqTransactionId)) {
            skipped++;
            continue;
          }
          const accountId = accountMap[bt.basiqAccountId] ?? undefined;
          const desc = bt.description || bt.category;
          newTransactions.push({
            id: generateId(),
            type: bt.type,
            amount: bt.amount,
            date: bt.date,
            category: bt.category,
            description: `[basiq:${bt.basiqTransactionId}] ${desc}`,
            taxDeductible: false,
            accountId,
            createdAt: new Date().toISOString(),
          });
          imported++;
        }

        return {
          ...prev,
          accounts: newAccounts,
          transactions: newTransactions,
        };
      });

      localStorage.setItem(BASIQ_LAST_SYNC_KEY, new Date().toISOString());

      const imported = basiqTxns.length;
      const syncResult: SyncResult = { imported, skipped: 0, accountsLinked };
      toast.success(`Synced ${imported} transactions`);
      return syncResult;
    } catch (e: any) {
      toast.error('Sync failed: ' + (e.message || 'Unknown error'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [basiqUserId, lastSync, invoke, updateData]);

  // Full connect flow: create user with stored mobile → open consent
  const startConnect = useCallback(async () => {
    setLoading(true);
    try {
      let uid = basiqUserId;
      if (!uid) {
        const mobile = await fetchMobile();
        uid = await createUser(mobile);
      }
      const url = await getConsentLink(uid);
      if (url) {
        // Try popup first, fall back to redirect if blocked
        const win = window.open(url, '_blank');
        if (win) {
          toast.info('Complete the bank connection in the new tab, then come back and sync.');
        } else {
          // Popup blocked — redirect current page instead
          toast.info('Redirecting to bank connection...');
          window.location.href = url;
        }
      } else {
        toast.error('No consent URL returned');
      }
    } catch {
      toast.error('Failed to start bank connection');
    } finally {
      setLoading(false);
    }
  }, [basiqUserId, createUser, getConsentLink, fetchMobile]);

  const disconnectBank = useCallback(async (connectionId: string) => {
    if (!basiqUserId) return;
    setLoading(true);
    try {
      await invoke({ action: 'disconnect', basiqUserId, connectionId });
      setConnections(prev => prev.filter(c => c.id !== connectionId));
      toast.success('Bank disconnected');
    } catch {
      toast.error('Failed to disconnect bank');
    } finally {
      setLoading(false);
    }
  }, [basiqUserId, invoke]);

  return {
    loading,
    connections,
    basiqAccounts,
    basiqUserId,
    lastSync,
    startConnect,
    checkStatus,
    syncTransactions,
    disconnectBank,
  };
}
